import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, money, fmtDate } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { EntriesTable } from "@/components/BillingCard";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { METHOD_LABEL } from "@/lib/constants";
import { WaiveDialog } from "@/components/AdminComp";

const RefundButton = ({ entry, onDone }) => {
  const max = Math.round((entry.amount - entry.refunded_amount) * 100) / 100;
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(max);
  const [reason, setReason] = useState("");
  const [writeOff, setWriteOff] = useState(true);
  if (entry.type !== "payment" || max <= 0) return null;
  const submit = async () => {
    try {
      await api.post(`/admin/ledger/${entry.id}/refund`, { amount: Number(amount), reason, write_off: writeOff });
      toast.success(entry.source === "stripe" ? "Refund sent to the customer's card" : "Refund recorded");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  if (!open) return <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setOpen(true)} data-testid={`refund-btn-${entry.id}`}>Refund</Button>;
  return (
    <div className="flex items-center gap-2" data-testid={`refund-form-${entry.id}`}>
      <Input type="number" step="0.01" min="0.01" max={max} value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 w-24" data-testid={`refund-amount-${entry.id}`} />
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="h-8 w-32" data-testid={`refund-reason-${entry.id}`} />
      <label className="flex items-center gap-1 whitespace-nowrap text-xs text-slate-600" title="Also lower what the customer owes by the same amount">
        <input type="checkbox" checked={writeOff} onChange={(e) => setWriteOff(e.target.checked)} data-testid={`refund-writeoff-${entry.id}`} /> Write off
      </label>
      <Button size="sm" className="bg-red-600 text-white hover:bg-red-700 hover:text-white" disabled={!(Number(amount) > 0 && Number(amount) <= max)} onClick={submit} data-testid={`refund-confirm-${entry.id}`}>Confirm</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  );
};

const EntryForm = ({ policyId, onDone }) => {
  const [f, setF] = useState({ type: "payment", amount: "", method: "cash", reference: "", note: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const submit = async () => {
    try {
      await api.post(`/admin/policies/${policyId}/ledger`, { ...f, amount: Number(f.amount), method: f.type === "payment" ? f.method : "adjustment" });
      toast.success("Entry recorded");
      setF({ ...f, amount: "", reference: "", note: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-6" data-testid="manual-entry-form">
      <div className="space-y-1 sm:col-span-1">
        <Label>Type</Label>
        <Select value={f.type} onValueChange={set("type")}>
          <SelectTrigger data-testid="entry-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="payment">Payment</SelectItem>
            <SelectItem value="charge">Charge (adds to balance)</SelectItem>
            <SelectItem value="credit">Credit (reduces balance)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Amount</Label>
        <Input type="number" step="0.01" min="0.01" value={f.amount} onChange={set("amount")} data-testid="entry-amount-input" />
      </div>
      <div className="space-y-1">
        <Label>Method</Label>
        <Select value={f.method} onValueChange={set("method")} disabled={f.type !== "payment"}>
          <SelectTrigger data-testid="entry-method-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["cash", "check", "money_order", "card"].map((m) => <SelectItem key={m} value={m}>{METHOD_LABEL[m]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Reference #</Label>
        <Input value={f.reference} onChange={set("reference")} placeholder="Check #, receipt…" data-testid="entry-reference-input" />
      </div>
      <div className="space-y-1">
        <Label>Note</Label>
        <Input value={f.note} onChange={set("note")} data-testid="entry-note-input" />
      </div>
      <div className="flex items-end">
        <Button className="w-full bg-blue-700 text-white hover:bg-blue-800 hover:text-white" disabled={!(Number(f.amount) > 0)} onClick={submit} data-testid="entry-submit-btn">Record</Button>
      </div>
    </div>
  );
};

export const BillingDialog = ({ policy, onDone }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/policies/${policy.id}/billing`).then((r) => setData(r.data)).catch((err) => toast.error(formatApiError(err)));
  }, [policy.id]);
  useEffect(() => {
    if (open) load();
  }, [open, load]);
  const changed = () => {
    load();
    onDone?.();
  };
  const b = data?.billing;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`admin-billing-btn-${policy.id}`}>Billing</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid="admin-billing-dialog">
        <DialogHeader><DialogTitle>Billing · {policy.policy_number} · {policy.insured_name}</DialogTitle></DialogHeader>
        {b && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 p-4 sm:grid-cols-6">
              {[["Status", <StatusPill key="s" status={b.billing_status} testId="admin-billing-status" />], ["Plan", b.payment_plan === "full" ? "Paid in full" : "Monthly"], ["Balance", money(b.balance)], ["Paid", money(b.paid)], ["Billed to date", money(b.scheduled_to_date + b.charges - b.credits)], ["Next due", b.next_due_date ? fmtDate(b.next_due_date) : "—"]].map(([k, v]) => (
                <div key={k}><p className="text-xs uppercase tracking-wider text-slate-500">{k}</p><div className="mt-1 font-mono font-semibold" data-testid={`admin-billing-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</div></div>
              ))}
            </div>
            <EntryForm policyId={policy.id} onDone={changed} />
            <WaiveDialog policyId={policy.id} balance={b.balance} onDone={changed} />
            {data.entries.length ? <EntriesTable entries={data.entries} showRef action={(e) => <RefundButton entry={e} onDone={changed} />} /> : <p className="text-sm text-slate-400">No entries yet.</p>}
            <DocumentsPanel policyId={policy.id} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DocsDialog = ({ quote }) => (
  <Dialog>
    <DialogTrigger asChild>
      <Button size="sm" variant="ghost" data-testid={`admin-docs-btn-${quote.id}`}>Docs</Button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Documents · {quote.full_name}</DialogTitle></DialogHeader>
      <DocumentsPanel quoteId={quote.id} compact />
    </DialogContent>
  </Dialog>
);
